import type { WaveBlock } from "@vida/wave-core";

export function collapsedBlocksStorageKey(waveId: string): string {
  return `wave.collapsed.${waveId}`;
}

export function hasCollapsedBlocksStorage(waveId: string): boolean {
  try {
    return localStorage.getItem(collapsedBlocksStorageKey(waveId)) !== null;
  } catch {
    return false;
  }
}

export function readCollapsedBlocks(waveId: string): Set<string> {
  try {
    const raw = localStorage.getItem(collapsedBlocksStorageKey(waveId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return new Set();
  }
}

export function writeCollapsedBlocks(waveId: string, ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(collapsedBlocksStorageKey(waveId), JSON.stringify([...ids]));
  } catch {
    // ignore quota / private mode
  }
}

/** Active nested widget ids (children with parentId). */
export function nestedBlockIds(blocks: WaveBlock[]): string[] {
  return blocks
    .filter((b) => b.payload.parentId && !b.payload.archived)
    .map((b) => b.id);
}

export function nestedBlockIdsForParent(blocks: WaveBlock[], parentId: string): string[] {
  return blocks
    .filter((b) => b.payload.parentId === parentId && !b.payload.archived)
    .map((b) => b.id);
}

/** Merge nested ids into collapsed set (first visit — not persisted until user toggles). */
export function mergeNestedCollapsedDefaults(
  prev: Set<string>,
  blocks: WaveBlock[],
): Set<string> {
  const nestedIds = nestedBlockIds(blocks);
  if (nestedIds.length === 0) return new Set(prev);
  const next = new Set(prev);
  let changed = false;
  for (const id of nestedIds) {
    if (!next.has(id)) {
      next.add(id);
      changed = true;
    }
  }
  return changed ? next : prev;
}
